<template>
  <div class="flex items-center justify-center p-2">
    <!-- Dropzone -->
    <div v-if="pages.length === 0"  
      @click="fileInput.click()"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      class="w-full max-w-4xl transition-all duration-500 cursor-pointer rounded-3xl flex flex-col items-center justify-center text-center relative overflow-hidden group"
      :class="[isDragging ? 'scale-[1.01]' : '']">
      
      <!-- Animated gradient border -->
      <div class="absolute inset-0 rounded-3xl p-[1.5px] overflow-hidden">
        <div class="absolute inset-0 rounded-3xl transition-opacity duration-500"
          :class="isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
          style="background: conic-gradient(from 0deg, #D01020, #104060, #D01020);"></div>
        <div class="absolute inset-0 rounded-3xl transition-opacity duration-500"
          :class="isDragging ? 'opacity-0' : 'opacity-100 group-hover:opacity-0'"
          style="background: #E0E0E0;"></div>
      </div>

      <!-- Inner card -->
      <div class="relative w-full rounded-[22px] py-10 px-10 transition-all duration-500 flex flex-col items-center bg-white border border-gray-100 shadow-lg">
        
        <!-- Subtle decorative glow -->
        <div class="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-32 rounded-full opacity-0 group-hover:opacity-40 transition-opacity duration-700 blur-3xl pointer-events-none" style="background: radial-gradient(circle, #D01020 0%, transparent 70%);"></div>

        <!-- Loading indicator -->
        <div v-if="isProcessing" class="absolute inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center z-50 rounded-[22px]">
          <div class="relative w-16 h-16 mb-5">
            <div class="absolute inset-0 rounded-full border-[3px] border-red-50"></div>
            <div class="absolute inset-0 rounded-full border-[3px] border-transparent border-t-[#D01020] animate-spin"></div>
          </div>
          <p class="text-gray-800 font-bold text-lg">Procesando {{ totalPagesBeingProcessed }} páginas...</p>
          <p class="text-gray-400 text-sm mt-1">Esto puede tomar un momento</p>
        </div>

        <div class="relative z-10 flex flex-col items-center">
          <!-- PDF Icon -->
          <div class="w-12 h-12 mb-2 flex items-center justify-center transition-all duration-300">
            <img :src="'/icon.png'" alt="PDF Icon" class="w-full h-full object-contain" />
          </div>
          
          <!-- Heading -->
          <span class="text-[19px] font-bold text-[#104060] mb-1">Divide tu archivo PDF</span>
          <p class="text-gray-400 text-[14px] mb-4 max-w-sm">Selecciona o arrastra tu archivo PDF para extraer páginas o dividirlo</p>

          <!-- Upload Button -->
          <button
            @click.stop="fileInput.click()"
            class="text-white text-[14px] font-semibold px-6 py-2.5 rounded-xl active:scale-[0.97] transition-all duration-200 flex items-center gap-2.5 shadow-lg shadow-red-200/60 hover:shadow-xl hover:shadow-red-300/60 bg-[#D01020] hover:bg-[#B01010]">
            <Upload :size="16" />
            <span>Seleccionar Archivo PDF</span>
          </button>
          
          <!-- Divider -->
          <div class="mt-7 mb-1 flex items-center gap-3 w-full max-w-xs">
            <div class="h-px flex-1 bg-gray-200"></div>
            <kbd class="px-2 py-0.5 bg-gray-50 border border-gray-200 rounded text-[10px] font-mono text-gray-500">CTRL + V</kbd>
            <p class="text-[10px] text-gray-400 font-medium uppercase tracking-widest">o arrastra aquí</p>
            <div class="h-px flex-1 bg-gray-200"></div>
          </div>
        </div>
      </div>
    </div>
    <!-- Main Editor -->
    <div v-else class="bg-white rounded-2xl w-full max-w-7xl shadow-xl border-t-4 border-[#D01020] mt-3 overflow-hidden">
      <div class="max-w-7xl mx-auto">
        <!-- Toolbar -->
        <div class="bg-slate-50 p-4 border-b border-slate-200">
          <div class="flex flex-wrap items-center gap-4">
              <div class="flex flex-col ml-2">
                <p class="text-[#104060] text-xl font-bold flex items-center gap-2">
                  <span class="truncate max-w-[280px]" :style="{ color: '#104060' }">
                    {{ currentFileName }}
                  </span>
                </p>
                <span class="text-slate-500 text-sm font-medium">({{ pages.length }} páginas en total)</span>
              </div>

            <div class="flex-1"></div>

            <!-- Page range input -->
            <div class="flex items-center gap-2 bg-white border border-slate-200 focus-within:border-[#D01020] focus-within:ring-4 focus-within:ring-rose-100/50 rounded-xl px-3 py-1.5 shadow-sm transition-all duration-200">
              <label for="range-input" class="text-xs font-bold text-[#104060] uppercase tracking-wider">Rango:</label>
              <input 
                id="range-input"
                type="text" 
                v-model="rangeText" 
                @input="onRangeInput"
                placeholder="Ej. 1-3, 5" 
                class="w-32 border-none bg-transparent p-0 text-sm font-medium text-slate-700 focus:ring-0 focus:outline-none focus:border-transparent outline-none placeholder-slate-400"
              />
            </div>
            
            <div class="flex items-center gap-2">
              <button @click="resetAll" class="bg-white hover:bg-slate-100 text-slate-600 px-4 py-2 rounded-xl border border-slate-200 flex items-center gap-2 font-semibold transition-all hover:shadow-sm">
                <RotateCcw :size="18" />
                {{ t('restart') }}
              </button>

              <button
                @click="saveAndDownload"
                :disabled="isDownloading || selectedUids.length === 0"
                class="relative bg-[#D01020] hover:bg-[#b01010] disabled:opacity-70 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-xl flex items-center gap-2 font-bold transition-all text-base shadow-lg shadow-rose-200">
                <!-- Loader -->
                <span v-if="isDownloading" class="flex items-center gap-2">
                  <svg class="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 100 16v-4l-3.5 3.5L12 24v-4a8 8 0 01-8-8z">
                    </path>
                  </svg>
                  {{ t('processing_btn') }}
                </span>

                <!-- Normal Button -->
                <span v-else class="flex items-center gap-2">
                  <Download :size="20" />
                  {{ t('download_pdf') }}
                </span>
              </button>
            </div>
          </div>
        </div>
        <!-- Processing pages message -->
        <div v-if="isProcessing" class="bg-[#104060]/5 border border-[#104060]/20 rounded-xl p-3 mb-2 flex items-center gap-3">
          <div class="animate-spin rounded-full h-5 w-5 border-2 border-[#104060]/30 border-t-[#D01020]"></div>
          <p class="text-[#104060] font-semibold">{{ t('processing_pages', { count: totalPagesBeingProcessed }) }}... {{ t('appear_shortly') }}</p>
        </div>
        <div class="flex items-start gap-4">
          <div ref="scrollContainer" 
            class="flex-1 overflow-y-auto p-6 mb-5 border-2 border-transparent transition-all duration-300 rounded-xl"
            :style="pages.length > 15 ? `max-height: ${maxHeight}px` : ''">
            <!-- Pages Grid -->
               <div class="max-h-[360px] grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6 gap-4">
                  <div 
                    v-for="element in pages" 
                    :key="element.uid"
                    class="relative group cursor-pointer" 
                    :style="{'--file-color': '#104060'}"
                    @click.stop="toggleSelect(element.uid)"
                  >
                    <div class="relative bg-white rounded-xl overflow-hidden transition-all duration-300 shadow-sm hover:shadow-xl border-2"
                      :class="[
                        selectedUids.includes(element.uid) ? 'border-[#D01020] ring-4 ring-rose-100' : 'border-slate-100 hover:border-[#104060]/30'
                      ]" 
                      :style="!selectedUids.includes(element.uid) ? { borderColor: '#10406020', borderLeftWidth: '4px', borderLeftColor: '#104060' } : { }"
                    >
                      <!-- Loading overlay -->
                      <div v-if="element.isLoading" class="absolute inset-0 bg-white/80 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-lg">
                        <div class="flex flex-col items-center gap-2">
                          <div class="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#D01020]"></div>
                          <p class="text-slate-600 text-xs font-bold uppercase tracking-tighter">Cargando...</p>
                        </div>
                      </div>

                      <!-- Thumbnail -->
                      <div class="aspect-square overflow-hidden bg-gray-100 flex items-center justify-center shadow-md">
                        <img 
                          :src="element.thumbnail" 
                          class="max-w-[90%] max-h-[90%] p-0 object-contain"
                          :style="{ transform: `rotate(${element.rotation}deg)` }"
                        />
                      </div>

                      <!-- Page number -->
                      <div class="absolute bottom-2 right-2 bg-black/50 backdrop-blur-md text-white px-2 py-0.5 rounded-md text-[10px] font-bold shadow-sm z-10 flex items-center gap-1">
                        {{ element.pageNum }}
                      </div>

                      <!-- Selection indicator -->
                      <div v-if="selectedUids.includes(element.uid)" class="absolute inset-0 bg-[#104060]/5 flex items-center justify-center pointer-events-none z-10">
                        <div class="bg-[#104060] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-xl shadow-lg transform scale-110">
                          <Check :size="17" strokeWidth="4" />
                        </div>
                      </div>

                      <!-- Hover actions -->
                      <div class="absolute inset-0 bg-black/20 transition-all rounded-lg opacity-0 group-hover:opacity-100 z-30">
                        <div class="absolute top-2 left-2 flex gap-1.5">
                          <button @click.stop="rotateSinglePage(element, -90)" class="p-[5px] bg-black/40 hover:bg-black/60 rounded-md text-white transition backdrop-blur-md shadow-sm" title="Rotar izquierda">
                            <RotateCcw :size="13" />
                          </button>
                          <button @click.stop="rotateSinglePage(element, 90)" class="p-[5px] bg-black/40 hover:bg-black/60 rounded-md text-white transition shadow-sm" title="Rotar derecha">
                            <RotateCw :size="13" />
                          </button>
                        </div>

                        <!-- Center Hint -->
                        <div class="absolute inset-0 flex items-end mb-2 justify-center pointer-events-none">
                          <p class="text-[8px] mb-0.5 font-bold text-white uppercase tracking-widest bg-black/50 px-2 py-0.5 rounded-full backdrop-blur-sm border border-white/10">
                            {{ selectedUids.includes(element.uid) ? 'Deseleccionar' : 'Seleccionar' }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
              </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Hidden file input -->
    <input
      type="file"
      ref="fileInput"
      accept="application/pdf"
      class="hidden"
      @change="onFileChange"
    />
  </div>
  <!-- Footer -->
  <transition name="slide-up">
    <div v-if="selectedUids.length > 0" class="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center justify-between bg-[#104060] p-4 rounded-3xl shadow-[0_20px_50px_rgba(16,64,96,0.3)] border border-white/10 w-[95%] max-w-4xl mx-auto z-40 backdrop-blur-lg">
      <!-- Left Section -->
      <div class="flex items-center gap-4">
        <div class="w-10 h-10 bg-[#D01020] text-white rounded-2xl text-xl font-bold shadow-lg flex items-center justify-center">
          {{ selectedUids.length }}
        </div>
        <div class="flex flex-col">
          <span class="text-white text-lg font-bold leading-none">
            {{ selectedUids.length === 1 ? t('page_selected') : t('pages_selected') }}
          </span>
          <div class="flex gap-3 mt-1">
            <button @click="selectAll" class="text-blue-200 text-xs font-bold uppercase tracking-widest hover:text-white transition-colors flex items-center gap-1"
              :disabled="selectedUids.length === pages.length">
              <Plus :size="12" /> {{ t('select_all') }}
            </button>
            <button @click="deselectAll" class="text-rose-300 text-xs font-bold uppercase tracking-widest hover:text-rose-200 transition-colors flex items-center gap-1"
              :disabled="selectedUids.length === 0">
              <X :size="12" /> {{ t('cancel_all') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Right Section: Actions -->
      <div class="flex items-center gap-3"> 
        <div class="h-10 w-px bg-white/10 mx-2"></div>
        <div class="flex items-center gap-2 bg-white/5 p-1.5 rounded-2xl border border-white/5">
          <button @click="previewSelected" class="p-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all hover:scale-105 active:scale-95" title="Vista previa">
            <Eye :size="20" />
          </button>
          <button @click="rotateSelected(-90)" class="p-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all hover:scale-105 active:scale-95" title="Rotar izquierda">
            <RotateCcw :size="20" />
          </button>
          <button @click="rotateSelected(90)" class="p-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all hover:scale-105 active:scale-95" title="Rotar derecha">
            <RotateCw :size="20" />
          </button>
        </div>
      </div>
    </div>
  </transition>
  <!-- Fullscreen Preview Modal -->
  <div v-if="showPreviewModal" class="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-50">
    <div class="w-full h-full flex flex-col overflow-hidden max-w-full max-h-full">

      <!-- Top Toolbar -->
      <div class="bg-[#104060] px-6 py-4 flex items-center justify-between shadow-2xl border-b border-white/10">
        <!-- Left Section -->
        <div class="flex items-center gap-3">
          <button @click="rotateCurrentPage(-90)" class="text-white p-3 hover:bg-white/10 rounded-2xl transition-all active:scale-90" title="Rotar izquierda">
            <RotateCcw :size="22" />
          </button>
          <button @click="rotateCurrentPage(90)" class="text-white p-3 hover:bg-white/10 rounded-2xl transition-all active:scale-90" title="Rotar derecha">
            <RotateCw :size="22" />
          </button>
        </div>

        <!-- Center Section -->
        <div class="flex items-center gap-6">
          <button @click="swiperInstance?.slidePrev()" 
            class="text-white/70 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            :disabled="swiperActiveIndex === 0">
            <ChevronLeft :size="28" />
          </button>
          
          <div class="flex items-center gap-3 text-white font-bold select-none">
             <span class="bg-white/10 px-2 py-1 rounded-md text-xl min-w-[40px] text-center border border-white/5">
             <span class="text-[22px]">{{ swiperActiveIndex + 1 }}</span>
             </span>
             <span class="text-white/30 text-2xl mx-1 font-light">/</span>
             <span class="text-[23px]">{{ previewPages.length }}</span>
          </div>

          <button @click="swiperInstance?.slideNext()" 
            class="text-white/70 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            :disabled="swiperActiveIndex === previewPages.length - 1">
            <ChevronRight :size="28" />
          </button>
        </div>

        <!-- Right Section -->
        <div class="flex items-center">
          <button @click="showPreviewModal = false" class="text-white/80 hover:text-white hover:bg-[#D01020] p-2 rounded-md transition-all active:scale-95">
            <X :size="28" />
          </button>
        </div>
      </div>

      <!-- Swiper Preview -->
      <div class="flex-1 flex items-center justify-center overflow-hidden bg-black/20 backdrop-blur-md">
            <Swiper
              :key="swiperRerenderKey"
              :modules="[Navigation, Pagination]"
              navigation
              pagination
              space-between="20"
              slides-per-view="1"
              class="w-full h-full"
              @swiper="onSwiper"
              @slideChange="onSlideChange"
              ref="swiperRef">
                    <SwiperSlide
              v-for="(page, index) in previewPages"
              :key="page.uid + '-' + page.rotation"
            >
            <div class="flex flex-col items-center justify-center w-full h-full p-4 md:p-8">
              <img
                :key="page.uid + '-' + page.rotation + '-' + (page.thumbnail ? page.thumbnail.substring(0,50) : '')"
                :src="page.thumbnail"
                :alt="`Página ${page.pageNum}`"
                class="max-w-full max-h-full object-contain shadow-2xl rounded-sm transition-transform duration-300"
                :style="{ transform: `rotate(${page.rotation}deg)` }"
              />
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { PDFDocument, degrees } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist'
import JSZip from 'jszip'
import { Upload, RotateCcw, RotateCw, Download, X, Plus, Eye, ChevronLeft, ChevronRight, Check } from 'lucide-vue-next'

const props = defineProps({
  translations: {
    type: Object,
    required: true
  }
})

const t = (key, replacements = {}) => {
  let translation = (props.translations && props.translations[key]) || key
  for (const [k, v] of Object.entries(replacements)) {
    translation = translation.replace(`:${k}`, v)
  }
  return translation
}

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs`;

import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

const swiperRef = ref(null)
const swiperInstance = ref(null)
const swiperRerenderKey = ref(0)
const swiperActiveIndex = ref(0)
const isDownloading = ref(false)

const pages = ref([])
const scrollContainer = ref(null)
const selectedUids = ref([])
const pdfjsDocRef = ref(null)
const pdfLibDocRef = ref(null)
const currentFileName = ref('')
const rangeText = ref('')
const previewPages = ref([])
const showPreviewModal = ref(false)
const isDragging = ref(false)
const isProcessing = ref(false)
const totalPagesBeingProcessed = ref(0)
let uidCounter = 0
const fileInput = ref(null)

const handleDrop = (e) => {
  isDragging.value = false
  const files = Array.from(e.dataTransfer.files)
  if (files.length > 0) {
    processFiles(files)
  }
}

const handlePaste = (e) => {
  const items = e.clipboardData?.items
  if (!items) return
  
  for (let item of items) {
    if (item.type === 'application/pdf' || item.type.startsWith('application/')) {
      const file = item.getAsFile()
      if (file && file.type === 'application/pdf') {
        processFiles([file])
        break
      }
    }
  }
}

const processFiles = async (files) => {
  const pdfFile = files.find(f => f.type === 'application/pdf')
  if (!pdfFile) {
    alert('Por favor, selecciona un archivo PDF.')
    return
  }

  resetAll()
  currentFileName.value = pdfFile.name
  isProcessing.value = true

  try {
    const buffer = await pdfFile.arrayBuffer()

    const pdfLibDoc = await PDFDocument.load(buffer)
    pdfLibDocRef.value = pdfLibDoc

    const pdfjsDoc = await pdfjsLib.getDocument({ data: new Uint8Array(buffer) }).promise
    pdfjsDocRef.value = pdfjsDoc

    totalPagesBeingProcessed.value = pdfjsDoc.numPages

    for (let i = 1; i <= pdfjsDoc.numPages; i++) {
      pages.value.push({
        uid: uidCounter++,
        fileName: pdfFile.name,
        pageNum: i,
        rotation: 0,
        thumbnail: '',
        isLoading: true
      })
    }

    // Auto-select all by default in Split PDF so the user can easily deselect or visually choose
    selectedUids.value = pages.value.map(p => p.uid)
    syncRangeTextFromSelection()

    let pageIndex = 0
    for (let i = 1; i <= pdfjsDoc.numPages; i++) {
      const { thumbnail } = await renderPage(pdfjsDoc, i, 0)
      if (pages.value[pageIndex]) {
        pages.value[pageIndex].thumbnail = thumbnail
        pages.value[pageIndex].isLoading = false
      }
      pageIndex++
    }
  } catch (error) {
    console.error('Error processing PDF:', error)
    alert('Error al procesar el archivo PDF. Inténtalo de nuevo.')
  }

  isProcessing.value = false
  totalPagesBeingProcessed.value = 0
}

const renderPage = async (pdfDoc, pageNum, rotation = 0) => {
  const page = await pdfDoc.getPage(pageNum);
  const viewport = page.getViewport({ scale: 2.5, rotation });
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  await page.render({ canvasContext: ctx, viewport }).promise;
  return { thumbnail: canvas.toDataURL("image/png") };
};

const rotateSinglePage = async (page, deg) => {
  try {
    page.isLoading = true
    const pdfjsDoc = pdfjsDocRef.value
    if (!pdfjsDoc) return

    page.rotation = (page.rotation + deg + 360) % 360
    const { thumbnail } = await renderPage(pdfjsDoc, page.pageNum, page.rotation)
    page.thumbnail = thumbnail
  } catch (error) {
    console.error('Error rotating page:', error)
  } finally {
    page.isLoading = false
  }
}

const onFileChange = (e) => {
  const files = Array.from(e.target.files)
  if (files.length) processFiles(files)
  e.target.value = ''
}

const toggleSelect = (uid) => {
  const idx = selectedUids.value.indexOf(uid)
  if (idx === -1) selectedUids.value.push(uid)
  else selectedUids.value.splice(idx, 1)
  
  // Sort selection to match original order
  selectedUids.value.sort((a, b) => {
    const pageA = pages.value.find(p => p.uid === a)
    const pageB = pages.value.find(p => p.uid === b)
    return pageA.pageNum - pageB.pageNum
  })
  
  syncRangeTextFromSelection()
}

const selectAll = () => {
  selectedUids.value = pages.value.map(p => p.uid)
  syncRangeTextFromSelection()
}

const deselectAll = () => {
  selectedUids.value = []
  syncRangeTextFromSelection()
}

const syncRangeTextFromSelection = () => {
  if (selectedUids.value.length === 0) {
    rangeText.value = ''
    return
  }

  // Get selected page numbers sorted
  const pageNums = selectedUids.value
    .map(uid => pages.value.find(p => p.uid === uid)?.pageNum)
    .filter(Boolean)
    .sort((a, b) => a - b)

  // Compress page numbers into ranges (e.g. 1,2,3,5 -> "1-3, 5")
  const ranges = []
  let start = pageNums[0]
  let prev = pageNums[0]

  for (let i = 1; i <= pageNums.length; i++) {
    const curr = pageNums[i]
    if (curr === prev + 1) {
      prev = curr
    } else {
      if (start === prev) {
        ranges.push(`${start}`)
      } else {
        ranges.push(`${start}-${prev}`)
      }
      start = curr
      prev = curr
    }
  }

  rangeText.value = ranges.join(', ')
}

const onRangeInput = () => {
  const text = rangeText.value.trim()
  if (!text) {
    selectedUids.value = []
    return
  }

  const maxPage = pages.value.length
  const matchedPages = new Set()
  const parts = text.split(',')

  for (const part of parts) {
    const trimmed = part.trim()
    const match = trimmed.match(/^(\d+)(?:\s*-\s*(\d+))?$/)
    if (match) {
      const start = parseInt(match[1], 10)
      const end = match[2] ? parseInt(match[2], 10) : start
      if (start > 0 && end > 0 && start <= maxPage && end <= maxPage) {
        const actualStart = Math.min(start, end)
        const actualEnd = Math.max(start, end)
        for (let i = actualStart; i <= actualEnd; i++) {
          matchedPages.add(i)
        }
      }
    }
  }

  // Map matched page numbers back to uids
  selectedUids.value = pages.value
    .filter(p => matchedPages.has(p.pageNum))
    .map(p => p.uid)
}

const rotateSelected = async (deg) => {
  try {
    const selectedPages = pages.value.filter(p => selectedUids.value.includes(p.uid))
    selectedPages.forEach(p => { p.isLoading = true })

    const renderPromises = selectedPages.map(async (page) => {
      try {
        const pdfjsDoc = pdfjsDocRef.value
        if (!pdfjsDoc) return
        page.rotation = (page.rotation + deg + 360) % 360
        const { thumbnail } = await renderPage(pdfjsDoc, page.pageNum, page.rotation)
        page.thumbnail = thumbnail
      } finally {
        page.isLoading = false
      }
    })

    await Promise.all(renderPromises)
  } catch (error) {
    console.error('Error in rotateSelected:', error)
    pages.value.forEach(p => {
      if (selectedUids.value.includes(p.uid)) {
        p.isLoading = false
      }
    })
  }
}

const previewSelected = () => {
  previewPages.value = pages.value.filter(p => selectedUids.value.includes(p.uid))
  swiperActiveIndex.value = 0
  showPreviewModal.value = true
  nextTick(() => {
    swiperRef.value?.swiper?.slideTo(0)
  })
}

const onSwiper = (swiper) => {
  swiperInstance.value = swiper
}

const onSlideChange = (swiper) => {
  swiperActiveIndex.value = swiper.activeIndex
}

const rotateCurrentPage = async (deg) => {
  const page = previewPages.value[swiperActiveIndex.value];
  if (!page) return;

  page.rotation = (page.rotation + deg + 360) % 360;

  const pdfjsDoc = pdfjsDocRef.value;
  if (pdfjsDoc) {
    page.isLoading = true;
    try {
      const { thumbnail } = await renderPage(pdfjsDoc, page.pageNum, page.rotation);
      page.thumbnail = thumbnail;
    } finally {
      page.isLoading = false;
    }
  }

  swiperRerenderKey.value++;
  await nextTick();
  if (swiperRef.value?.swiper) {
    swiperRef.value.swiper.slideTo(swiperActiveIndex.value, 0);
  }
};

const saveAndDownload = async () => {
  if (selectedUids.value.length === 0) return

  try {
    isDownloading.value = true
    await nextTick()
    await new Promise(resolve => setTimeout(resolve, 50))

    const origPdf = pdfLibDocRef.value
    if (!origPdf) return

    const selectedPages = pages.value.filter(p => selectedUids.value.includes(p.uid))
    const zip = new JSZip()
    const rawName = currentFileName.value.replace(/\.pdf$/i, '')

    for (const page of selectedPages) {
      const singlePdf = await PDFDocument.create()
      const [copiedPage] = await singlePdf.copyPages(origPdf, [page.pageNum - 1])
      if (page.rotation !== 0) {
        copiedPage.setRotation(degrees(page.rotation))
      }
      singlePdf.addPage(copiedPage)
      const pdfBytes = await singlePdf.save()
      
      zip.file(`${rawName}-pagina-${page.pageNum}.pdf`, pdfBytes)
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    const url = URL.createObjectURL(zipBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `dividido-${rawName}-${Date.now()}.zip`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
    alert('Error al dividir el PDF y generar el archivo ZIP. Inténtalo de nuevo.')
  } finally {
    isDownloading.value = false
  }
}

const resetAll = () => {
  pages.value = []
  selectedUids.value = []
  pdfjsDocRef.value = null
  pdfLibDocRef.value = null
  currentFileName.value = ''
  rangeText.value = ''
  uidCounter = 0
}

const maxHeight = computed(() => {
  const itemHeight = 228
  const gap = 16
  const width = window.innerWidth
  let columns = 2
  if (width >= 640) columns = 3
  if (width >= 768) columns = 4
  if (width >= 1024) columns = 5
  if (width >= 1280) columns = 5

  const rowsFor15Items = Math.ceil(15 / columns)
  return rowsFor15Items * itemHeight + (rowsFor15Items - 1) * gap
})

onMounted(() => {
  window.addEventListener('paste', handlePaste)
})

onUnmounted(() => {
  window.removeEventListener('paste', handlePaste)
  document.body.style.overflow = ''
})

watch(showPreviewModal, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})
</script>

<style scoped>
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

#range-input,
#range-input:focus {
  border: 0 !important;
  outline: none !important;
  box-shadow: none !important;
  ring: 0 !important;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateX(-50%) translateY(24px);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateX(-50%) translateY(0);
  opacity: 1;
}
:deep(.swiper-button-next),
:deep(.swiper-button-prev) {
  color: #D01020 !important;
}

:deep(.swiper-pagination-bullet) {
  background: #D01020 !important;
  opacity: 0.4;
}

:deep(.swiper-pagination-bullet-active) {
  background: #D01020 !important;
  opacity: 1;
}
</style>
